import React from "react";
import API from "../api/index";
import {
    Typography,
    TextField,
    Button,
    Autocomplete,
    CircularProgress,
    Box,
    IconButton,
    Stack,
    Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { dataGridStyle } from "../theme";
import { DataGrid } from "@mui/x-data-grid";
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { startToast, updateToastSuccess, updateToastWarning, updateToastError } from '../utils/notification-loading';


const SuppressionView = ({ suppData }) => {
    const [selectedDetail, setSelectedDetail] = useState("")
    const [suppNotification, setSuppNotification] = useState(false)
    const [suppVisualization, setSuppVisualization] = useState(false)
    const [selectedIp, setSelectedIp] = useState({ ip1: 'ANY', ip2: 'ANY' })
    const [errors, setErrors] = useState({ errorIp1: false, errorIp2: false })
    const [singleDetail, setSingleDetail] = useState([])
    const [autocompleteLoading, setAutocompleteLoading] = useState(true)
    const [suppressedEventsList, setSuppressedEventsList] = useState([])
    const [forViewPrevSuppressed, setForViewPrevSuppressed] = useState(false)
    const [, setChargedSuppressedEvents] = useState(false)
    const [searchedDetail, setSearchedDetail] = useState('')


    useEffect(() => {
        if (suppData) {
            setSelectedDetail(suppData.dettaglio ?? "")
            setSelectedIp({ ip1: suppData.ipSource, ip2: suppData.ipDestination })
            setAutocompleteLoading(false);
        }
    }, [suppData]);

    useEffect(() => {   
        setAutocompleteLoading(true)
        const abortController = new AbortController();
        const { signal } = abortController;
        API.getSingleDetailofType2and3({ detail: searchedDetail, source: selectedIp.ip1, destination: selectedIp.ip2 }, signal).then(res => {
            const onlyValueOfDetail = res.data.map(item => item._id)
            setSingleDetail(onlyValueOfDetail)
            setAutocompleteLoading(false)
        }).catch(err => {
            if (err.message !== 'canceled') {
                console.error('[SuppressionView] Failed to fetch detail list:', err)
                setAutocompleteLoading(false)
            }
        })
        return () => { abortController.abort() }
    }, [searchedDetail, selectedIp.ip1, selectedIp.ip2])

    // Un campo IP vuoto non è "ANY" -- se lo accettassimo come valido finirebbe
    // scritto così com'è nella regola (source/destination vuoti in tabella,
    // come per `detail`), invece di richiedere all'utente di rimettere "ANY".
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$|^ANY$/;

    const handleChangeIp1 = (event) => {
        const { value } = event.target;
        setErrors({ ...errors, errorIp1: !ipPattern.test(value) })
        setSelectedIp({ ...selectedIp, ip1: value });
    };

    const handleChangeIp2 = (event) => {
        const { value } = event.target;
        setErrors({ ...errors, errorIp2: !ipPattern.test(value) })
        setSelectedIp({ ...selectedIp, ip2: value });
    };

    const handleSubmitSuppression = () => {
        // Difesa in profondità: il bottone è già disabilitato in questo caso
        // (vedi `disabled` su Autocomplete/Apply), ma un dettaglio vuoto qui
        // creerebbe una regola "jolly" che sopprime tutto -- non basarsi solo
        // sullo stato del bottone.
        if (!selectedDetail || selectedDetail.trim() === '') {
            return updateToastWarning('Choose the log to suppress')
        }
        startToast();
        if(suppNotification || suppVisualization){
            const payload = {
                "detail": selectedDetail,
                "ipSource": selectedIp.ip1,
                "ipDest": selectedIp.ip2,
                "mode": suppNotification && suppVisualization ? 2 : suppNotification ? 1 : suppVisualization ? 0 : null
            }
            API.postSoppressEvent(payload).then(response => {
                updateToastSuccess("Suppression rule added")
                setSelectedDetail("")
                setSuppNotification(false)
                setSuppVisualization(false)
                setSelectedIp({ ip1: 'ANY', ip2: 'ANY' })

            }).catch(err => updateToastError("Could not add the suppression rule"))
        }else{
            updateToastWarning('Choose what to suppress')
        }
    }

    const handleInput = (event) => {
        event.target.value = event.target.value.toUpperCase();
    };

    const ToViewSuppressedEvents = () => {
        if (forViewPrevSuppressed) {
            setForViewPrevSuppressed(false)
        } else {
            setForViewPrevSuppressed(true)
            API.getSuppressedEventsofType2and3().then(res => {
                const transformedData = res.data.map(item => ({
                    id: item._id,
                    ...item,
                    modeN: item.mode,
                    mode: item.mode === 0 ? 'Hidden in table' : item.mode === 1 ? 'Notifications' : 'Notifications and table'
                }))
                setSuppressedEventsList(transformedData)
                setChargedSuppressedEvents(true)
            }).catch(err => {
                console.error('[ToViewSuppressedEvents] Failed to fetch suppressed events:', err)
                setChargedSuppressedEvents(true)
            })
        }
    }

    const ToDeleteSuppressionEvent = (event) => {
        startToast();
        const payload = {
            "detail": event.row.dettaglio,
            "ipSource": event.row.ipAtt,
            "ipDest": event.row.ipDest,
            "mode": event.row.modeN
        }
        API.delSuppressedEvent(payload).then(res => {
            updateToastSuccess("Suppression rule removed");
            const updatedList = suppressedEventsList.filter(item => {
                return !(
                    item.dettaglio === event.row.dettaglio && item.ipAtt === event.row.ipAtt && item.ipDest === event.row.ipDest && item.modeN === event.row.modeN
                )
            })
            setSuppressedEventsList(updatedList)
        }).catch(err => updateToastError("Could not remove the suppression rule"))
    }

    const columns = [
        {
            field: "deleteButton",
            headerName: 'REMOVE',
            headerAlign: 'center',
            flex: 1,
            minWidth: 100,
            align: 'center',
            renderCell: (cellValues) => (
                <Tooltip title="Remove rule and restore the log">
                    <IconButton onClick={() => { ToDeleteSuppressionEvent(cellValues) }}>
                    <SettingsBackupRestoreIcon />
                </IconButton>
                    </Tooltip>
            )
        },
        {
            field: "mode",
            headerName: "SUPPRESSES",
            minWidth: 170,
            headerAlign: "left",
            align: "left",
        },
        {
            field: "ipAtt",
            headerName: "SOURCE",
            minWidth: 130,
            headerAlign: "left",
            align: "left",
        },
        {
            field: "ipDest",
            headerName: "DESTINATION",
            minWidth: 130,
            headerAlign: "left",
            align: "left",
        },
        {
            field: "dettaglio",
            headerName: "LOG",
            flex: 1,
            minWidth: 400,
            headerAlign: "left",
            align: "left",
            renderCell: (params) => {
                let cellStyle = {};
                const cellValueLower = params.value.toLowerCase();
                if (
                    cellValueLower.includes("malware ") ||
                    cellValueLower.includes("metasploit ") ||
                    cellValueLower.includes("zerologon ") ||
                    cellValueLower.includes("ernalblue ") ||
                    cellValueLower.includes("eternalblue ") ||
                    cellValueLower.includes("mirai ") ||
                    cellValueLower.includes("cobalt ") ||
                    cellValueLower.includes("wannacry ") ||
                    cellValueLower.includes("reverse shell ") ||
                    cellValueLower.includes("mware ")
                ) {
                    cellStyle = { color: "red" };
                } else if (
                    cellValueLower.includes("scan ") ||
                    cellValueLower.includes("mining ") ||
                    cellValueLower.includes("powershell ") ||
                    cellValueLower.includes("pivot ") ||
                    cellValueLower.includes("shellcode ")
                ) {
                    cellStyle = { color: "yellow" };
                } else if (
                    cellValueLower.includes("trojan ") ||
                    cellValueLower.includes("system32 ") ||
                    cellValueLower.includes("hkey ") ||
                    cellValueLower.includes("bin ") ||
                    cellValueLower.includes("sh ") ||
                    cellValueLower.includes("cve ") ||
                    cellValueLower.includes("polymor ") ||
                    cellValueLower.includes("nbios ") ||
                    cellValueLower.includes("buffer ") ||
                    cellValueLower.includes("exploit ")
                ) {
                    cellStyle = { color: "orange" };
                }
                return (
                    <div style={{ ...cellStyle, width: "auto" }}>{params.value}</div>
                );
            },
        },
    ]

    const ACCENT = "#449095";
    const invalid = errors.errorIp1 || errors.errorIp2;
    const hasDetail = !!selectedDetail?.trim();
    const noMode = !suppNotification && !suppVisualization;
    const canApply = !invalid && hasDetail && !noMode;

    const tab = (active) => ({
        px: 2, py: 0.6, borderRadius: 4, fontSize: "0.72rem", fontWeight: 700,
        border: `1px solid ${active ? ACCENT : "#2a2a2a"}`,
        color: active ? ACCENT : "#777",
        backgroundColor: active ? "rgba(69,144,149,0.1)" : "transparent",
        "&:hover": { backgroundColor: "rgba(69,144,149,0.12)", borderColor: ACCENT, color: ACCENT },
    });

    const sectionLabel = (text) => (
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: ACCENT, letterSpacing: "0.12em", mb: 1 }}>{text}</Typography>
    );

    const modeCard = (checked, onToggle, Icon, title, description) => (
        <Box
            role="checkbox"
            aria-checked={checked}
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onToggle(); } }}
            sx={{
                flex: 1, cursor: "pointer", p: 1.75, borderRadius: 1.5, outline: "none",
                border: `1px solid ${checked ? ACCENT : "#2a2a2a"}`,
                backgroundColor: checked ? "rgba(69,144,149,0.1)" : "#0f0f0f",
                transition: "all 0.15s",
                "&:hover, &:focus-visible": { borderColor: ACCENT },
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Icon sx={{ fontSize: 18, color: checked ? ACCENT : "#777" }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: checked ? "#e8e8e8" : "#999" }}>{title}</Typography>
            </Stack>
            <Typography sx={{ fontSize: "0.74rem", color: checked ? "#aaa" : "#666", lineHeight: 1.4 }}>{description}</Typography>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: "55vh" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography sx={{ fontSize: "0.82rem", color: "#999", maxWidth: 520 }}>
                    Stop a recurring log from notifying you or showing up in the tables, for a given source and destination.
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<PlaylistAddIcon fontSize="small" />} sx={tab(!forViewPrevSuppressed)}
                        onClick={() => forViewPrevSuppressed && ToViewSuppressedEvents()}>New rule</Button>
                    <Button size="small" startIcon={<ListAltIcon fontSize="small" />} sx={tab(forViewPrevSuppressed)}
                        onClick={() => !forViewPrevSuppressed && ToViewSuppressedEvents()}>Active rules</Button>
                </Stack>
            </Stack>

            {forViewPrevSuppressed ? (
                <Box sx={{ height: "50vh" }}>
                    <DataGrid
                        rows={suppressedEventsList}
                        hideFooter
                        columns={columns}
                        sx={{ ...dataGridStyle }}
                        localeText={{ noRowsLabel: "No suppression rules yet" }}
                    />
                </Box>
            ) : (
                <Stack spacing={2.5}>
                    <Box>
                        {sectionLabel("TRAFFIC")}
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <TextField
                                value={selectedIp.ip1}
                                onChange={handleChangeIp1}
                                error={errors.errorIp1}
                                fullWidth size="small"
                                label="Source IP"
                                helperText={errors.errorIp1 ? "Enter an IP address or ANY" : "Use ANY to match every source"}
                                slotProps={{ input: { onInput: handleInput } }}
                            />
                            <TextField
                                value={selectedIp.ip2}
                                onChange={handleChangeIp2}
                                error={errors.errorIp2}
                                fullWidth size="small"
                                label="Destination IP"
                                helperText={errors.errorIp2 ? "Enter an IP address or ANY" : "Use ANY to match every destination"}
                                slotProps={{ input: { onInput: handleInput } }}
                            />
                        </Stack>
                    </Box>

                    <Box>
                        {sectionLabel("LOG TO SUPPRESS")}
                        <Autocomplete
                            id="autocomplete"
                            size="small"
                            value={selectedDetail}
                            options={singleDetail}
                            loading={autocompleteLoading}
                            onChange={(event, newValue) => {
                                setSelectedDetail(newValue ?? "")
                            }}
                            onInput={(e) => {
                                setSelectedDetail(e.target.value)
                                setSearchedDetail(e.target.value)
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Log detail"
                                    placeholder="Search or paste the log detail"
                                    helperText="Pick one of the logs seen for this source and destination"
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {autocompleteLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box>
                        {sectionLabel("WHAT TO SUPPRESS")}
                        <Stack direction="row" spacing={1.5} sx={{ opacity: hasDetail ? 1 : 0.5, pointerEvents: hasDetail ? "auto" : "none" }}>
                            {modeCard(suppNotification, () => setSuppNotification((v) => !v), NotificationsOffOutlinedIcon, "Notifications", "Stop sending notifications for this log.")}
                            {modeCard(suppVisualization, () => setSuppVisualization((v) => !v), VisibilityOffOutlinedIcon, "Table", "Hide this log from the security tables.")}
                        </Stack>
                        {hasDetail && noMode && (
                            <Typography sx={{ color: "#c0392b", fontSize: "0.76rem", mt: 1 }}>Choose at least one option.</Typography>
                        )}
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        disabled={!canApply}
                        onClick={handleSubmitSuppression}
                        sx={{ backgroundColor: ACCENT, "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}
                    >
                        Add suppression rule
                    </Button>
                </Stack>
            )}
        </Box>
    );
};

export default SuppressionView;
